/**
 * Measurement: what a workspace's webinar pages report to.
 *
 * Two ids, set once in Settings > Scripts: a Google Analytics measurement id
 * and a Meta pixel id, each with its own on switch. The server writes the tags
 * into the register page's head from them (`measurementHead`), and the watch
 * page loads the same tags at run time (src/lib/measure.ts). We write those
 * tags ourselves, from ids we understood.
 *
 * Other tools are the host's call: whatever they paste goes into the head of
 * their registration pages as pasted, after our own tags, so a tool we do not
 * know (a visitor-identification script, say) can run there too.
 *
 * The register page checks what loaded and says so through its beacon
 * (`pageChecks`), so Settings can show each part working without a test.
 *
 * Two things ride along without a setting:
 *   • a team's own visits: open any page with `?internal=1` once per browser,
 *     and that browser's visits carry `traffic_type: internal`, which the
 *     Analytics internal-traffic filter drops. The marker cookie is set on the
 *     host's own domain, and any `*_internal=1` cookie counts, so a marker a
 *     host's website already sets is honoured too.
 *   • `?debug=1` puts the events in Analytics DebugView.
 */

import type { MetaContent } from "./meta-events";
import type { MeasurementConfig } from "./types";
export type { MeasurementConfig };

export const EMPTY_MEASUREMENT: MeasurementConfig = { ga_measurement_id: null, meta_pixel_id: null };

/** A Google Analytics 4 measurement id: G- and a short run of letters and digits. */
export const GA_ID_RE = /^G-[A-Z0-9]{6,16}$/;
/** A Meta pixel id: a run of digits. */
export const PIXEL_ID_RE = /^\d{10,20}$/;

/** The ids as the host typed them, cleaned; an id that is not one is refused. */
export function normalizeMeasurement(raw: {
  ga_measurement_id?: unknown;
  meta_pixel_id?: unknown;
}): { ok: true; value: MeasurementConfig } | { ok: false; error: string } {
  const ga = typeof raw.ga_measurement_id === "string" ? raw.ga_measurement_id.trim().toUpperCase() : "";
  const px = typeof raw.meta_pixel_id === "string" ? raw.meta_pixel_id.trim() : "";
  if (ga && !GA_ID_RE.test(ga)) {
    return { ok: false, error: "That measurement ID doesn't look right. It starts with G- and comes from Analytics under Admin and Data streams." };
  }
  if (px && !PIXEL_ID_RE.test(px)) {
    return { ok: false, error: "That pixel ID doesn't look right. It is a run of digits from Meta Events Manager." };
  }
  return { ok: true, value: { ga_measurement_id: ga || null, meta_pixel_id: px || null } };
}

/** The ids as saved, and whether each is switched on; anything unreadable is nothing saved. */
export function measurementSettingsFrom(json: string | null | undefined): MeasurementConfig & { ga_on: boolean; meta_on: boolean } {
  try {
    const v = json ? (JSON.parse(json) as Record<string, unknown>) : {};
    const got = normalizeMeasurement(v);
    const ids = got.ok ? got.value : { ...EMPTY_MEASUREMENT };
    return { ...ids, ga_on: v.ga_off !== true, meta_on: v.meta_off !== true };
  } catch {
    return { ...EMPTY_MEASUREMENT, ga_on: true, meta_on: true };
  }
}

/**
 * What the pages report to: an id switched off reads as no id, so nothing that
 * reads this (the pages, the watch page, our server's events) sends to it.
 */
export function measurementFrom(json: string | null | undefined): MeasurementConfig {
  const m = measurementSettingsFrom(json);
  return { ga_measurement_id: m.ga_on ? m.ga_measurement_id : null, meta_pixel_id: m.meta_on ? m.meta_pixel_id : null };
}

/**
 * Where a workspace's own system wants each registration handed to, when it
 * asked for that: an https address on the measurement row, set by hand, never
 * from Settings. The person and the visitor id their site minted go there.
 */
export function identifyUrlFrom(json: string | null | undefined): string | null {
  if (!json) return null;
  try {
    const v = JSON.parse(json) as { identify_url?: unknown };
    const url = typeof v.identify_url === "string" ? v.identify_url.trim() : "";
    return /^https:\/\/[^\s"'<>]+$/.test(url) ? url : null;
  } catch {
    return null;
  }
}

/** The most a host may paste as one script, and the longest name it may have. */
export const SCRIPT_CODE_MAX = 20000;
const SCRIPT_NAME_MAX = 60;

/**
 * A pasted script as typed, cleaned: the code is the host's call, so only its
 * size is checked. A script with no name is called "Custom script".
 */
export function normalizeScript(raw: { name?: unknown; code?: unknown }): { ok: true; name: string; code: string } | { ok: false; error: string } {
  const code = typeof raw.code === "string" ? raw.code.trim() : "";
  if (!code) return { ok: false, error: "Paste the code your tool gave you." };
  if (code.length > SCRIPT_CODE_MAX) return { ok: false, error: "That is too long. Keep a script under 20,000 characters." };
  const name = (typeof raw.name === "string" ? raw.name.trim() : "").slice(0, SCRIPT_NAME_MAX) || "Custom script";
  return { ok: true, name, code };
}

// Why: addresses that name a format, not a place anything loads from.
const NOT_LOADED = new Set(["www.w3.org", "w3.org", "schema.org"]);

/** The hosts a pasted script loads from, as far as its code says: at most five. */
export function scriptHosts(code: string): string[] {
  const out = new Set<string>();
  for (const m of code.matchAll(/(?:https?:)?\/\/([a-z0-9-]+(?:\.[a-z0-9-]+)+)(?=[/:?"'#\s]|$)/gi)) {
    const host = m[1].toLowerCase();
    if (!NOT_LOADED.has(host)) out.add(host);
    if (out.size >= 5) break;
  }
  return [...out];
}

/** What the register page checks once it has loaded, and the version it answers for. */
interface PageChecks {
  v: string;
  ga: boolean;
  pixel: boolean;
  scripts: Array<{ id: string; hosts: string[] }>;
}

/** A short, steady fingerprint of a string (FNV-1a), in base 36. */
function shortHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/**
 * The checks for a page carrying these tags and scripts, or null when it
 * carries none. The version changes with anything that changes what loads, so
 * a report from a page opened before a change is not counted against it.
 */
export function pageChecks(m: MeasurementConfig, scripts: Array<{ id: string; code: string }>): PageChecks | null {
  if (!m.ga_measurement_id && !m.meta_pixel_id && scripts.length === 0) return null;
  const v = shortHash(JSON.stringify([m.ga_measurement_id, m.meta_pixel_id, scripts.map((s) => [s.id, shortHash(s.code)])]));
  return {
    v,
    ga: m.ga_measurement_id !== null,
    pixel: m.meta_pixel_id !== null,
    scripts: scripts.map((s) => ({ id: s.id, hosts: scriptHosts(s.code) })),
  };
}

export function measurementActive(m: MeasurementConfig): boolean {
  return m.ga_measurement_id !== null || m.meta_pixel_id !== null;
}

/** The link tags on the register page carry, at most this long each. */
export const ATTRIBUTION_MAX = 200;
/** What is kept from the link a person arrived on. */
export const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "li_fat_id",
  "ttclid",
  "msclkid",
  "gbraid",
  "wbraid",
  "vid",
] as const;
type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string>> & {
  /** The page they arrived on, path and query, no host. */
  landing?: string;
  /** Unix seconds of that first visit. */
  first_seen?: number;
  /** The page that sent them here, when it was not our own. */
  ref?: string;
  /** Our own visitor id for this browser: the thread from the page's visits to the person. */
  frv?: string;
  /** How many times this browser opened the page, this one included. */
  visits?: number;
  /** Seconds from the page opening to the form being sent. */
  seconds?: number;
  /** Seconds from the page opening to the first keystroke in the form. */
  started?: number;
};

/** A count the page reported: a whole number, or nothing. */
function wholeNumber(v: unknown, min: number): number | undefined {
  return typeof v === "number" && Number.isFinite(v) && v >= min ? Math.round(v) : undefined;
}

/** An attribution object as the page or a caller hands it in, cleaned; null when it says nothing. */
export function attributionFrom(raw: unknown): Attribution | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const out: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const v = r[key];
    if (typeof v === "string" && v.trim()) out[key] = v.trim().slice(0, ATTRIBUTION_MAX);
  }
  if (typeof r.landing === "string" && r.landing.trim()) out.landing = r.landing.trim().slice(0, 500);
  if (typeof r.first_seen === "number" && Number.isFinite(r.first_seen) && r.first_seen > 0) {
    out.first_seen = Math.round(r.first_seen);
  }
  if (typeof r.ref === "string" && r.ref.trim()) out.ref = r.ref.trim().slice(0, 500);
  if (typeof r.frv === "string" && /^[A-Za-z0-9_-]{4,64}$/.test(r.frv.trim())) out.frv = r.frv.trim();
  const visits = wholeNumber(r.visits, 1);
  if (visits !== undefined) out.visits = visits;
  const seconds = wholeNumber(r.seconds, 0);
  if (seconds !== undefined) out.seconds = seconds;
  const started = wholeNumber(r.started, 0);
  if (started !== undefined) out.started = started;
  return Object.keys(out).length > 0 ? out : null;
}

/** Where a person came from, as a host's own system is handed it with the person. */
export interface AttributionBlock {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  li_fat_id: string | null;
  ttclid: string | null;
  msclkid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  /** The page they arrived on, path and query. */
  landing: string | null;
  /** Unix seconds of that first visit. */
  first_seen: number | null;
  /** Where they came from, in the host's words: the link's tag, the sending site, or Direct. */
  source: string | null;
}

/**
 * The attribution block for the webhook and the identify call. Why: the host's
 * own system can then tie a paying customer back to the ad that brought them.
 */
export function attributionBlock(raw: string | Attribution | null | undefined, source: string | null): AttributionBlock {
  let a: Attribution | null = null;
  if (typeof raw === "string") {
    try {
      a = attributionFrom(JSON.parse(raw));
    } catch {
      a = null;
    }
  } else if (raw) {
    a = raw;
  }
  const v = (k: AttributionKey) => a?.[k] ?? null;
  return {
    utm_source: v("utm_source"),
    utm_medium: v("utm_medium"),
    utm_campaign: v("utm_campaign"),
    utm_content: v("utm_content"),
    utm_term: v("utm_term"),
    fbclid: v("fbclid"),
    gclid: v("gclid"),
    li_fat_id: v("li_fat_id"),
    ttclid: v("ttclid"),
    msclkid: v("msclkid"),
    gbraid: v("gbraid"),
    wbraid: v("wbraid"),
    landing: a?.landing ?? null,
    first_seen: a?.first_seen ?? null,
    source: source?.trim() || null,
  };
}

/** Text safe inside a `<script>`: an id is letters and digits, but never trust it. */
function scriptSafe(s: string): string {
  return s.replace(/[^A-Za-z0-9_-]/g, "");
}

/**
 * The script every measured page runs first: the internal marker, the debug
 * switch, and the first-touch attribution, kept where the page can read them.
 * Six seconds after the page loads it says which tags and pasted scripts
 * loaded (`window.frChecks`), at most every half hour per browser while what
 * loads is unchanged. Plain ES5 so the model-designed page never has to know
 * anything about it.
 */
export const MEASURE_RUNTIME = `(function(){
  var W=window,D=document,L=location,q=L.search||"";
  function cookie(n){var m=D.cookie.match("(^|; )"+n+"=([^;]*)");return m?decodeURIComponent(m[2]):"";}
  function domain(){var h=L.hostname.split(".");if(h.length<3)return "";var top=h.slice(-2).join(".");return top==="flashsuite.net"?"":"."+top;}
  var d=domain(),tail="; path=/; max-age=31536000; SameSite=Lax; Secure"+(d?"; domain="+d:"");
  if(/[?&]internal=1(&|$)/.test(q)){try{D.cookie="fr_internal=1"+tail;}catch(e){}}
  W.frInternal=/(^|; )[A-Za-z0-9]*_?internal=1(;|$)/.test(D.cookie);
  W.frDebug=/[?&]debug=1(&|$)/.test(q);
  var keys=["utm_source","utm_medium","utm_campaign","utm_content","utm_term","fbclid","gclid","li_fat_id","ttclid","msclkid","gbraid","wbraid","vid"],got={},any=false;
  try{var p=new URLSearchParams(q);for(var i=0;i<keys.length;i++){var v=p.get(keys[i]);if(v){got[keys[i]]=String(v).slice(0,200);any=true;}}}catch(e){}
  var kept=null;try{var raw=cookie("fr_attr");if(raw)kept=JSON.parse(raw);}catch(e){kept=null;}
  if(any&&!kept){got.landing=(L.pathname+q).slice(0,500);got.first_seen=Math.round(Date.now()/1000);kept=got;try{D.cookie="fr_attr="+encodeURIComponent(JSON.stringify(kept))+"; path=/; max-age=2592000; SameSite=Lax; Secure";}catch(e){}}
  W.frAttribution=kept;
  var T0=Date.now(),ref=(D.referrer||"").slice(0,500);
  try{if(ref&&new URL(ref).hostname===L.hostname)ref="";}catch(e){ref="";}
  function store(k,v){try{localStorage.setItem(k,v);return true;}catch(e){return false;}}
  function load(k){try{return localStorage.getItem(k)||"";}catch(e){return "";}}
  var frv=load("fr_v")||cookie("fr_v");
  if(!frv){frv="v"+Math.random().toString(36).slice(2,10)+Date.now().toString(36);if(!store("fr_v",frv)){try{D.cookie="fr_v="+frv+tail;}catch(e){}}}
  var vk="fr_vis:"+L.pathname,visits=(parseInt(load(vk)||"0",10)||0)+1;store(vk,String(visits));
  var started=0,sent={};
  function slugOf(){var f=D.querySelector("form[data-register]");if(!f)return "";var a=f.getAttribute("action")||"",i=a.indexOf("/api/w/");if(i<0)return "";var rest=a.slice(i+7),j=rest.indexOf("/");return j<0?"":rest.slice(0,j);}
  function beacon(kind,extra){var s=slugOf();var key=kind+((extra&&extra.href)||"");if(!s||sent[key])return;sent[key]=1;
    var b={kind:kind,frv:frv,ref:ref,returning:visits>1?1:0};if(kept){b.utm_source=kept.utm_source||"";b.utm_medium=kept.utm_medium||"";b.utm_campaign=kept.utm_campaign||"";}
    for(var k in extra||{})b[k]=extra[k];var url="/api/w/"+s+"/beacon",body=JSON.stringify(b);
    try{if(navigator.sendBeacon&&navigator.sendBeacon(url,new Blob([body],{type:"application/json"})))return;}catch(e){}
    try{fetch(url,{method:"POST",headers:{"content-type":"application/json"},body:body,keepalive:true});}catch(e){}}
  function page(){var o={};for(var k in kept||{})o[k]=kept[k];o.frv=frv;o.visits=visits;if(ref)o.ref=ref;o.seconds=Math.round((Date.now()-T0)/1000);if(started)o.started=Math.round((started-T0)/1000);return JSON.stringify(o);}
  function hid(f){if(!f.id)f.id="fr-register";var all=D.getElementsByName("attribution");for(var j=0;j<all.length;j++)if(all[j].getAttribute("form")===f.id)return all[j];
    var h=D.createElement("input");h.type="hidden";h.name="attribution";h.setAttribute("form",f.id);(D.querySelector("[data-fr-door]")||D.body).appendChild(h);return h;}
  function fill(){var forms=D.querySelectorAll("form[data-register]");for(var i=0;i<forms.length;i++){var f=forms[i];
    var inp=hid(f);inp.value=page();
    f.addEventListener("input",function(){if(!started){started=Date.now();beacon("form_start");}},true);
    f.addEventListener("submit",function(){hid(f).value=page();},true);}
    beacon("visit",{landing:(L.pathname+q).slice(0,500)});}
  D.addEventListener("click",function(e){var t=e.target;var a=t&&t.closest?t.closest("a[href]"):null;if(!a||a.closest("form[data-register]"))return;
    var h=a.getAttribute("href")||"";if(!h||h.charAt(0)==="#")return;beacon("link",{label:(a.textContent||"").replace(/ +/g," ").trim().slice(0,80),href:h.slice(0,300)});},true);
  function chk(){var c=W.frChecks;if(!c||!c.v)return;
    var last=load("fr_chk").split("|");if(last[0]===c.v&&Date.now()-(+last[1]||0)<1800000)return;
    var res=[];try{res=performance.getEntriesByType("resource")||[];}catch(e){}
    function seen(h){for(var i=0;i<res.length;i++){var n=String(res[i].name||""),st=res[i].responseStatus;if((n.indexOf("//"+h+"/")>-1||n.indexOf("//"+h+"?")>-1)&&!(st>=400))return true;}return false;}
    var parts={},s=c.scripts||[];
    if(c.ga)parts.ga=!!W.google_tag_manager;
    if(c.pixel&&!W.frInternal)parts.pixel=!!(W.fbq&&typeof W.fbq.callMethod==="function");
    for(var i=0;i<s.length;i++){var ok=!s[i].hosts.length;for(var j=0;j<s[i].hosts.length&&!ok;j++)ok=seen(s[i].hosts[j]);parts["s:"+s[i].id]=ok;}
    store("fr_chk",c.v+"|"+Date.now());beacon("checks",{v:c.v,parts:parts});}
  W.addEventListener("load",function(){setTimeout(chk,6000);});
  if(D.readyState==="loading")D.addEventListener("DOMContentLoaded",fill);else fill();
})();`;

/** The Google tag, configured from what the runtime found. */
function gaHead(id: string): string {
  const safe = scriptSafe(id);
  return (
    `<script async src="https://www.googletagmanager.com/gtag/js?id=${safe}"></script>` +
    `<script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());` +
    `var frCfg={};if(window.frInternal)frCfg.traffic_type='internal';if(window.frDebug)frCfg.debug_mode=true;` +
    `gtag('config','${safe}',frCfg);</script>`
  );
}

/** A value written into a `<script>` as JSON, with nothing that could close the tag. */
function scriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

/**
 * The Meta pixel, with its PageView, naming the webinar when the page is one.
 * The team's own browsers send Meta nothing.
 */
function pixelHead(id: string, content?: MetaContent): string {
  const safe = scriptSafe(id);
  const about = content ? `,${scriptJson(content)}` : "";
  return (
    `<script>if(!window.frInternal){!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};` +
    `if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];` +
    `s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
    `fbq('init','${safe}');fbq('track','PageView'${about});}</script>` +
    `<noscript><img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${safe}&ev=PageView&noscript=1"/></noscript>`
  );
}

/**
 * Everything the register page's head carries for measurement. The runtime
 * always goes first: the tags read what it found, and the attribution is kept
 * whether or not any tag is set. The checks ride with it when anything loads.
 */
export function measurementHead(m: MeasurementConfig, content?: MetaContent, checks?: PageChecks | null): string {
  const pre = checks ? `window.frChecks=${scriptJson(checks)};` : "";
  const parts = [`<script data-measure>${pre}${MEASURE_RUNTIME}</script>`];
  if (m.ga_measurement_id) parts.push(gaHead(m.ga_measurement_id));
  if (m.meta_pixel_id) parts.push(pixelHead(m.meta_pixel_id, content));
  return parts.join("");
}
